import React from "react";
import Tooltip from "./Tooltip";
import { resolveNationalities } from "../../utils/nationalityFlags";

/**
 * Every citizenship a person holds, as flags, in the platform's priority order.
 *
 * Product rule (Marin, 2026-08-11): a player shows ALL of his citizenships and
 * the country whose NATIONAL TEAM he plays for comes FIRST. Jude Bellingham
 * (born England, Irish passport, plays for England) must read "England,
 * Ireland" — the old passport-only field showed him as Irish alone.
 *
 * The order is decided in the backend
 * (`dashboard/utils/nationality_resolver.py`) and travels as `nationalities`;
 * `resolveNationalities` falls back to the historical
 * `nationality` + `birth_country` pair for payloads that predate it, so nobody
 * ends up flagless while both contracts are live.
 *
 * Mobile behaviour (UI rule 1): the row NEVER shrinks or drops a flag. Below
 * `max` it wraps onto a second line inside its own cell — flags are 16-20px
 * wide and a player holds at most three, so two per line fit at 375px.
 * `max={1}` is the narrow-list mode: one flag, the rest named in the tooltip.
 *
 * @param {Array|object} nationalities Either the resolved list or the whole row
 *   (`{ nationalities, nationality, birth_country }`) to resolve from.
 * @param {number} max Hard cap on rendered flags. The remainder still shows up
 *   in the tooltip, so information is hidden, never lost.
 * @param {string} className Per-surface flag sizing; the default matches the
 *   search/roster tables the pattern came from.
 */
const NationalityFlags = ({
  nationalities,
  max = 0,
  className = "sm:w-5 sm:h-4 w-4 h-3 object-cover border border-gray-200 rounded-sm",
  wrapperClassName = "flex flex-wrap items-center gap-2",
  tooltipPosition = "bottom-left",
}) => {
  const all = Array.isArray(nationalities)
    ? nationalities
    : resolveNationalities(nationalities);

  if (!all.length) return null;

  const shown = max > 0 ? all.slice(0, max) : all;
  const nameOf = (c) => (typeof c === "string" ? c : c?.name) || "";
  const imageOf = (c) => (typeof c === "string" ? null : c?.image_url || c?.flag_url);
  const keyOf = (c, i) =>
    (typeof c === "string" ? c : c?.id ?? c?.code ?? c?.name ?? imageOf(c)) || i;

  // One tooltip text for the whole group: the countries in priority order, so a
  // truncated row still tells the user which passports were left out.
  const label = all.map(nameOf).filter(Boolean).join(" · ");

  return (
    <div className={wrapperClassName}>
      {shown.map((country, index) => {
        const src = imageOf(country);
        if (!src) return null;
        return (
          <Tooltip
            key={keyOf(country, index)}
            content={label}
            position={tooltipPosition}
          >
            <img
              src={src}
              alt=""
              title={label}
              className={className}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Tooltip>
        );
      })}
    </div>
  );
};

export default NationalityFlags;
